import React, { Suspense, useEffect, useMemo } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import type { ApolloClient } from '@apollo/client';
import { ReduxRouter } from '@lagunovsky/redux-react-router';
import { RawIntlProvider, type IntlShape } from 'react-intl';
import { ToastHandler } from '@xarvio/toast';
import { createAppConfig } from './createAppConfig';
import { loadApolloAppContent } from './ApolloAppContentLoader';
import { useAsync } from 'react-use';
import { createModuleIntl, type LoadMomentLocale } from './createModuleIntl';
import {
  FullPageLoadingOverlayFallback,
  FullPageLoadingOverlayProvider,
} from './FullPageLoadingOverlay/FullPageLoadingOverlay';
import {
  localeSelector,
  countrySelector,
} from '@xarvio/frontend-core-state/lib/intl/selectors';
import {
  decodedUserTokenSelector,
  getIsUserLoggedInSelector,
} from '@xarvio/frontend-core-state/lib/auth/selectors';
import intlSlice from '@xarvio/frontend-core-state/lib/intl/intlSlice';
import { selectedFarmUuidSelector } from '@xarvio/frontend-core-state/lib/selectedFarm/selectors';
import type { AppFeaturesConfig } from '@xarvio/frontend-core-state/lib/featureFlags/featureFlagsSlice';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
  type RouteObject,
} from 'react-router-dom';

import { RouteProvider } from '@xarvio/frontend-core-router/lib/RouteContext';
import { AuthRoutes } from '@xarvio/frontend-core-router/lib/AuthRoutes';
import type {
  UseGetPageUrl,
  UseGeneratePageUrl,
} from '@xarvio/frontend-core-router/lib/types';
import {
  getUnprefixedRouteRedirectTarget,
  resolveLocaleRedirectTarget,
} from './localePathGuard';
import { getUnprefixedRedirectRoutePaths } from './unprefixedRedirectRoutes';

const UNPREFIXED_AUTH_ROUTE_PATHS = Object.values(AuthRoutes);

const ApolloAppContent = React.lazy(loadApolloAppContent);

const RouteAwareFullPageLoadingOverlayProvider: React.FC<
  React.PropsWithChildren<{
    isFullPageOverlayEnabled?: (pathname: string) => boolean;
  }>
> = ({ children, isFullPageOverlayEnabled }) => {
  const { pathname } = useLocation();

  return (
    <FullPageLoadingOverlayProvider
      isOverlayEnabled={isFullPageOverlayEnabled?.(pathname) ?? true}
    >
      {children}
    </FullPageLoadingOverlayProvider>
  );
};

type GetExtraRoutes = (args: {
  guestRedirectPath: string;
}) => Array<Pick<RouteObject, 'path' | 'element' | 'index'>>;

interface InnerAppWrapperProps
  extends Omit<
    AppWrapperProps,
    'apolloClient' | 'featuresConfig' | 'loadMomentLocale'
  > {
  isInitDataLoading: boolean;
  useGetPageUrl?: UseGetPageUrl;
  useGeneratePageUrl?: UseGeneratePageUrl;
}

const RouteProviderWrapper: React.FC<{
  children: React.ReactNode;
  useGetPageUrl?: UseGetPageUrl;
  useGeneratePageUrl?: UseGeneratePageUrl;
}> = ({ children, useGetPageUrl, useGeneratePageUrl }) => {
  const navigate = useNavigate();
  const farmUuid = useSelector(selectedFarmUuidSelector);
  const locale = useSelector(localeSelector);
  const country = useSelector(countrySelector);

  // If no hooks provided, just render children without RouteProvider
  if (!useGetPageUrl) {
    return <>{children}</>;
  }

  return (
    <RouteProvider
      useGetPageUrl={useGetPageUrl}
      useGeneratePageUrl={useGeneratePageUrl}
      navigate={navigate}
      currentParams={{
        farmUuid: farmUuid ?? undefined,
        locale,
        country,
      }}
    >
      {children}
    </RouteProvider>
  );
};

// Fallback redirect for unmatched routes: countryCode/languageCode query params (e.g. password-reset emails) → Redux intl locale → defaultRedirectPath.
const DefaultRedirect: React.FC<{ defaultRedirectPath: string }> = ({
  defaultRedirectPath,
}) => {
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const storeCountry = useSelector(countrySelector);
  const storeLocale = useSelector(localeSelector);

  // 1. Try query parameters (e.g. password-reset emails)
  const countryCode = searchParams.get('countryCode');
  const languageCode = searchParams.get('languageCode');

  const target = getUnprefixedRouteRedirectTarget({
    countryCode,
    defaultRedirectPath,
    languageCode,
    pathname,
    search,
    storeCountry,
    storeLocale,
  });

  return <Navigate to={target} replace />;
};

// Layout guard: redirects /:country/:locale mismatches (e.g. /de/en_jp) to the token locale, re-dispatching intl so store and URL stay consistent.
const LocalePathGuard: React.FC = () => {
  const { country = '', locale = '', '*': restPath } = useParams();
  const { search } = useLocation();
  const decodedToken = useSelector(decodedUserTokenSelector);
  const dispatch = useDispatch();

  const target = resolveLocaleRedirectTarget(country, locale, decodedToken);
  const targetLocale = target?.locale;

  useEffect(() => {
    if (targetLocale) {
      dispatch(intlSlice.actions.changeLocale(targetLocale));
    }
  }, [targetLocale, dispatch]);

  if (target) {
    const suffix = restPath ? `/${restPath}` : '';
    return (
      <Navigate
        to={`/${target.country}/${target.locale}${suffix}${search}`}
        replace
      />
    );
  }

  return <Outlet />;
};

const EmailVerifiedRoute: React.FC<{
  emailVerifiedRouteElement: React.ReactNode;
}> = ({ emailVerifiedRouteElement }) => {
  const isLoggedIn = useSelector(getIsUserLoggedInSelector);
  const { country = '', locale = '' } = useParams();

  if (!isLoggedIn) {
    return (
      <Navigate to={`/${country}/${locale}/${AuthRoutes.LOGIN}`} replace />
    );
  }

  return emailVerifiedRouteElement;
};

export const IntlContentLoader: React.FC<{
  children: (args: {
    intl: IntlShape | undefined;
    isLoading: boolean;
  }) => React.ReactNode;
  loadMomentLocale?: LoadMomentLocale;
}> = ({ children, loadMomentLocale }) => {
  const locale = useSelector(localeSelector);
  const { value: intl, loading } = useAsync(async () => {
    return createModuleIntl({ loadMomentLocale, locale });
  }, [loadMomentLocale, locale]);

  return children({ intl, isLoading: loading });
};

const InnerAppWrapper: React.FC<
  React.PropsWithoutRef<InnerAppWrapperProps>
> = ({
  appChrome,
  children,
  defaultRedirectPath = '/de/en-de',
  getExtraRoutes,
  unprefixedRedirectPaths,
  loadingFallback,
  isInitDataLoading,
  useGetPageUrl,
  useGeneratePageUrl,
  emailVerifiedRouteElement,
  acceptTermsRouteElement,
}) => {
  const effectiveDefaultRedirectPath = defaultRedirectPath;

  if (isInitDataLoading) {
    return loadingFallback ?? <FullPageLoadingOverlayFallback />;
  }

  const extraRoutes = getExtraRoutes
    ? getExtraRoutes({ guestRedirectPath: effectiveDefaultRedirectPath })
    : [];

  return (
    <>
      <ToastHandler />
      {appChrome}
      <RouteProviderWrapper
        useGetPageUrl={useGetPageUrl}
        useGeneratePageUrl={useGeneratePageUrl}
      >
        <Routes>
          {getUnprefixedRedirectRoutePaths(
            UNPREFIXED_AUTH_ROUTE_PATHS,
            unprefixedRedirectPaths,
          ).map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <DefaultRedirect
                  defaultRedirectPath={effectiveDefaultRedirectPath}
                />
              }
            />
          ))}
          <Route path=":country/:locale/*" element={<LocalePathGuard />}>
            {extraRoutes.map((route, i) => (
              <Route
                key={i}
                index={route.index}
                path={route.index ? undefined : route.path}
                element={route.element}
              />
            ))}
            {emailVerifiedRouteElement && (
              <Route
                path={AuthRoutes.EMAIL_VERIFIED}
                element={
                  <EmailVerifiedRoute
                    emailVerifiedRouteElement={emailVerifiedRouteElement}
                  />
                }
              />
            )}
            {acceptTermsRouteElement && (
              <Route
                path={AuthRoutes.ACCEPT_TC}
                element={acceptTermsRouteElement}
              />
            )}
            {children}
          </Route>
          <Route
            path="*"
            element={
              <DefaultRedirect
                defaultRedirectPath={effectiveDefaultRedirectPath}
              />
            }
          />
        </Routes>
      </RouteProviderWrapper>
    </>
  );
};

interface AppWrapperProps {
  children: React.ReactNode;
  appChrome?: React.ReactNode;
  /** Rendered inside Provider/Router but outside the loading gate (e.g. StagingPanel). */
  persistentChrome?: React.ReactNode;
  apolloPersistentChrome?: React.ReactNode;
  appContentWrapper?: React.ComponentType<React.PropsWithChildren>;
  isFullPageOverlayEnabled?: (pathname: string) => boolean;
  isApolloEnabled?: (pathname: string) => boolean;
  shouldRenderWhileInitDataLoading?: (pathname: string) => boolean;
  defaultRedirectPath?: string;
  getExtraRoutes?: GetExtraRoutes;
  unprefixedRedirectPaths?: string[];
  apolloClient?: ApolloClient;
  loadMomentLocale?: LoadMomentLocale;
  loadingFallback?: React.ReactNode;
  featuresConfig?: AppFeaturesConfig;
  emailVerifiedRouteElement?: React.ReactNode;
  acceptTermsRouteElement?: React.ReactNode;
  useGetPageUrl?: UseGetPageUrl;
  useGeneratePageUrl?: UseGeneratePageUrl;
}

interface AppContentProps
  extends Omit<AppWrapperProps, 'featuresConfig' | 'persistentChrome'> {
  apolloPersistentChrome?: React.ReactNode;
  store: {
    getState: () => unknown;
  };
}

const AppContent: React.FC<AppContentProps> = ({
  apolloClient,
  apolloPersistentChrome,
  appChrome,
  children,
  defaultRedirectPath,
  emailVerifiedRouteElement,
  getExtraRoutes,
  unprefixedRedirectPaths,
  isApolloEnabled,
  shouldRenderWhileInitDataLoading,
  loadMomentLocale,
  loadingFallback,
  acceptTermsRouteElement,
  store,
  useGeneratePageUrl,
  useGetPageUrl,
}) => {
  const { pathname } = useLocation();
  const isApolloRequired = isApolloEnabled?.(pathname) ?? true;
  const isInitDataBlocking =
    shouldRenderWhileInitDataLoading?.(pathname) !== true;
  const fallback = loadingFallback ?? <FullPageLoadingOverlayFallback />;
  const renderInnerApp = (
    isInitDataLoading: boolean,
    intl: IntlShape | undefined,
    isIntlLoading: boolean,
  ) => {
    if ((isInitDataLoading && isInitDataBlocking) || isIntlLoading || !intl) {
      return fallback;
    }

    return (
      <RawIntlProvider value={intl}>
        <InnerAppWrapper
          appChrome={appChrome}
          defaultRedirectPath={defaultRedirectPath}
          getExtraRoutes={getExtraRoutes}
          unprefixedRedirectPaths={unprefixedRedirectPaths}
          isInitDataLoading={isInitDataLoading && isInitDataBlocking}
          loadingFallback={loadingFallback}
          useGetPageUrl={useGetPageUrl}
          useGeneratePageUrl={useGeneratePageUrl}
          emailVerifiedRouteElement={emailVerifiedRouteElement}
          acceptTermsRouteElement={acceptTermsRouteElement}
        >
          {children}
        </InnerAppWrapper>
      </RawIntlProvider>
    );
  };

  return (
    <IntlContentLoader loadMomentLocale={loadMomentLocale}>
      {({ intl, isLoading }) =>
        isApolloRequired ? (
          <Suspense fallback={fallback}>
            <ApolloAppContent
              apolloClient={apolloClient}
              persistentChrome={apolloPersistentChrome}
              renderContent={(isInitDataLoading) =>
                renderInnerApp(isInitDataLoading, intl, isLoading)
              }
              store={store}
            />
          </Suspense>
        ) : (
          renderInnerApp(false, intl, isLoading)
        )
      }
    </IntlContentLoader>
  );
};

export const AppWrapper: React.FC<AppWrapperProps> = ({
  children,
  appChrome,
  persistentChrome,
  apolloPersistentChrome,
  appContentWrapper,
  isFullPageOverlayEnabled,
  isApolloEnabled,
  shouldRenderWhileInitDataLoading,
  defaultRedirectPath,
  getExtraRoutes,
  unprefixedRedirectPaths,
  apolloClient,
  loadMomentLocale,
  loadingFallback,
  featuresConfig,
  useGetPageUrl,
  useGeneratePageUrl,
  emailVerifiedRouteElement,
  acceptTermsRouteElement,
}) => {
  const { store, history } = useMemo(() => {
    const appConfig = createAppConfig({ featuresConfig });
    return appConfig;
  }, [featuresConfig]);

  const appContent = (
    <>
      {persistentChrome}
      <AppContent
        apolloClient={apolloClient}
        apolloPersistentChrome={apolloPersistentChrome}
        appChrome={appChrome}
        defaultRedirectPath={defaultRedirectPath}
        getExtraRoutes={getExtraRoutes}
        unprefixedRedirectPaths={unprefixedRedirectPaths}
        isApolloEnabled={isApolloEnabled}
        shouldRenderWhileInitDataLoading={shouldRenderWhileInitDataLoading}
        loadMomentLocale={loadMomentLocale}
        loadingFallback={loadingFallback}
        store={store}
        useGetPageUrl={useGetPageUrl}
        useGeneratePageUrl={useGeneratePageUrl}
        emailVerifiedRouteElement={emailVerifiedRouteElement}
        acceptTermsRouteElement={acceptTermsRouteElement}
      >
        {children}
      </AppContent>
    </>
  );

  return (
    <Provider store={store}>
      <ReduxRouter history={history}>
        <RouteAwareFullPageLoadingOverlayProvider
          isFullPageOverlayEnabled={isFullPageOverlayEnabled}
        >
          {appContentWrapper
            ? React.createElement(appContentWrapper, undefined, appContent)
            : appContent}
        </RouteAwareFullPageLoadingOverlayProvider>
      </ReduxRouter>
    </Provider>
  );
};
